import { apm } from "@elastic/apm-rum";
import { mm } from "@inovitas/infra3dsdk";
import { IconButton, Tooltip } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import { Cursor as CursorIcon } from "shared/common/utils/icons/measurementIcons";
import CursorContent from "./CursorContent";

export default function Cursor() {
  const { t } = useTranslation();

  const viewer = defaultStore((state) => state.viewer);
  const loading = defaultStore((state) => state.loading);
  const activeCampaign = defaultStore((state) => state.activeCampaign);
  const changeAlertMessage = defaultStore((state) => state.changeAlertMessage);

  const cursorPopover = sidebarStore((state) => state.cursorPopover);
  const changeCursorPopover = sidebarStore(
    (state) => state.changeCursorPopover
  );
  const activeCursorTool = sidebarStore((state) => state.activeCursorTool);

  const [canMeasure, setCanMeasure] = useState<boolean>(false);
  const [cursorTool, setCursorTool] = useState<mm.Cursor3d>();
  const [enabled, setEnabled] = useState<boolean>(false);

  //enable or disable measure mode and edit tool
  const checkMeasureMode = () => {
    // What does this name mean? 9
    const measureMode = viewer
      ?.getScene()
      .getMeasureModeHandler()
      .getMeasureMode();
    if (
      cursorTool &&
      (measureMode === mm.MEASUREMODE.MULTIVIEW ||
        measureMode === mm.MEASUREMODE.PANODEPTHMAPINTERSECTION ||
        measureMode ===
          mm.MEASUREMODE.PANODEPTHMAPINTERSECTION_MULTIVIEW_COMBINED ||
        measureMode === mm.MEASUREMODE.PANO_TWO_CLICK ||
        measureMode ===
          mm.MEASUREMODE.PANODEPTHMAPINTERSECTION_PANO_TWO_CLICK_COMBINED)
    ) {
      setCanMeasure(true);
      cursorTool.disable();
      changeCursorPopover(false);
    } else {
      setCanMeasure(false);
      if (cursorTool && cursorTool.enabled) {
        enableOrDisableTool(false);
      }
    }
  };

  //if one of cursorPopover, activeCampaign and loading state are changed than check measure mode
  useEffect(() => {
    if (viewer) {
      checkMeasureMode();
    }
  }, [cursorPopover, activeCampaign, loading]);

  //enable or disable cursor tool
  const handleClickCursor = () => {
    if (!canMeasure) {
      stopShowTool();
      if (cursorTool && cursorTool.enabled) {
        enableOrDisableTool(false);
      } else {
        enableOrDisableTool(true);
      }
    } else {
      changeAlertMessage({
        type: "warning",
        message: t("measurement.blocked_tool_message"),
      });
    }
  };

  // return icon for active cursor tool or by default cursor icon
  const icon = () => {
    if (activeCursorTool) {
      return activeCursorTool.icon;
    } else {
      return <CursorIcon />;
    }
  };

  //cursor tool options
  const cursorOptions = {
    quality: false,
  };

  //enable or disable cursor tool
  const enableOrDisableTool = (state: boolean) => {
    if (viewer && cursorTool) {
      //----------- 3d cursor ------------------------------------------
      if (state) {
        if (!cursorTool.enabled) {
          cursorTool.enable();
          setEnabled(true);
        }
      } else {
        cursorTool.disable();
        setEnabled(false);
      }
      //----------------------------------------------------------------
    }
  };

  // add timeout and remove it, to open cursor popover on hover after 1 second
  let wait: NodeJS.Timeout | null = null;
  const handleShowTool = () => {
    if (!canMeasure) {
      wait = setTimeout(() => {
        changeCursorPopover(true);
      }, 1000);
    }
  };
  const stopShowTool = () => {
    if (wait) {
      clearTimeout(wait);
    }
  };

  // change cursor tool from ring to point cloud or reverse
  useEffect(() => {
    if (activeCursorTool && cursorTool) {
      enableOrDisableTool(true);
      if (activeCursorTool.id === "points") {
        cursorTool.options.pointcloudbydefault = true;
      } else {
        cursorTool.options.pointcloudbydefault = false;
      }
    } else {
      enableOrDisableTool(false);
    }
  }, [activeCursorTool]);

  // add cursor options when viewer is defined
  useEffect(() => {
    if (viewer === undefined) return;
    setCursorTool(
      viewer
        .getScene()
        .getToolHandler()
        .createCursor3d(cursorOptions, "3dcursor")
    );
  }, [viewer]);

  return (
    <>
      <Tooltip
        title={cursorPopover ? "" : t("tooltip.cursor")}
        placement="right"
        enterDelay={400}
        classes={{
          tooltip: "infra3d-buttonDescription",
        }}
      >
        <IconButton
          className="onboarding-cursor"
          onMouseEnter={handleShowTool}
          onMouseLeave={stopShowTool}
          sx={{
            width: "40px",
            height: "40px",
            overflow: "hidden",
            mx: "auto",
            color: enabled ? "secondary.main" : "white",
            "@media (hover: hover)": {
              ":hover": { color: "secondary.main" },
            },
          }}
          classes={{
            root: cursorPopover
              ? "infra3d-iconButtonRippleActive"
              : "infra3d-iconButtonRipple",
          }}
          onClick={() =>
            monitor(apm, "change-status-cursor | Toolbar", handleClickCursor)
          }
          disableRipple={true}
        >
          {icon()}
        </IconButton>
      </Tooltip>
      {cursorPopover && <CursorContent />}
    </>
  );
}
